import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, X, Download, CheckSquare, Loader2 } from "lucide-react";

/**
 * Reusable multi-selection hook.
 * @param {Array<{id: string}>} items
 * @returns selection utilities
 */
export const useBulkSelection = (items) => {
  const [selected, setSelected] = useState(new Set());
  const allIds = useMemo(() => items.map((i) => i.id), [items]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === allIds.length && allIds.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  };

  const clear = () => setSelected(new Set());

  const isSelected = (id) => selected.has(id);
  const allChecked = allIds.length > 0 && selected.size === allIds.length;
  const someChecked = selected.size > 0 && !allChecked;

  return {
    selected,
    selectedIds: Array.from(selected),
    toggleOne,
    toggleAll,
    clear,
    isSelected,
    allChecked,
    someChecked,
    count: selected.size,
  };
};

/**
 * Floating bulk-action bar.
 * Props:
 *  - count: number of selected items
 *  - onClear: callback to deselect all
 *  - onDelete: async callback to delete selected (moves them to trash)
 *  - onExport: optional async callback to export as Excel
 *  - customActions: array of {label, icon, onClick, variant, testid}
 */
export const BulkActionBar = ({
  count,
  onClear,
  onDelete,
  onExport,
  customActions = [],
  itemLabel = "élément",
}) => {
  const [working, setWorking] = useState(false);

  if (count === 0) return null;

  const handleDelete = async () => {
    if (!window.confirm(`Déplacer ${count} ${itemLabel}(s) vers la corbeille ?\n\nVous pourrez les restaurer pendant 15 jours depuis /corbeille.`)) return;
    setWorking(true);
    try { await onDelete(); } finally { setWorking(false); }
  };

  const handleExport = async () => {
    setWorking(true);
    try { await onExport(); } finally { setWorking(false); }
  };

  return (
    <div className="sticky top-0 z-20 mb-4 bg-indigo-50 border border-indigo-200 rounded-xl p-3 shadow-sm flex items-center justify-between flex-wrap gap-2" data-testid="bulk-action-bar">
      <div className="flex items-center gap-3">
        <CheckSquare className="w-5 h-5 text-indigo-600" />
        <span className="text-sm font-medium text-indigo-800">
          {count} {itemLabel}{count > 1 ? "s" : ""} sélectionné{count > 1 ? "s" : ""}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {customActions.map((a, i) => (
          <Button
            key={i}
            size="sm"
            variant={a.variant || "outline"}
            onClick={async () => { setWorking(true); try { await a.onClick(); } finally { setWorking(false); } }}
            disabled={working}
            className={a.className || ""}
            data-testid={a.testid}
          >
            {a.icon && <a.icon className="w-4 h-4 mr-1.5" />}
            {a.label}
          </Button>
        ))}
        {onExport && (
          <Button size="sm" variant="outline" onClick={handleExport} disabled={working} data-testid="bulk-export-btn">
            <Download className="w-4 h-4 mr-1.5" />
            Exporter
          </Button>
        )}
        {onDelete && (
          <Button size="sm" variant="outline" onClick={handleDelete} disabled={working} className="border-red-200 text-red-600 hover:bg-red-50" data-testid="bulk-delete-btn">
            {working ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
            Corbeille
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onClear} data-testid="bulk-clear-btn">
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/** Small wrapper for the "select all" header checkbox */
export const BulkSelectAllCheckbox = ({ allChecked, someChecked, onToggle, ...props }) => (
  <Checkbox
    checked={allChecked ? true : someChecked ? "indeterminate" : false}
    onCheckedChange={onToggle}
    data-testid="bulk-select-all"
    {...props}
  />
);
